// components/project/EconomicImportModal.js
// Modal per importare dati ufficiali nel Quadro Economico (Contratto, Extra, Arredi)
// Solo Admin/Administration

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  Download, FileText, Package, Sofa, Loader2, AlertTriangle,
  CheckCircle, XCircle, Info, Building2
} from 'lucide-react';
import api from '../../lib/api';

// Labels per stati import
const IMPORT_STATUS_LABELS = {
  available: { label: 'Da importare', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  already_imported: { label: 'Già importato', color: 'bg-gray-100 text-gray-500', icon: XCircle },
  not_importable: { label: 'Non importabile', color: 'bg-red-100 text-red-600', icon: AlertTriangle }
};

// Labels per source types
const SOURCE_TYPE_LABELS = {
  contract: { label: 'Contratto', icon: FileText, color: 'text-blue-600' },
  extra: { label: 'Extra', icon: Package, color: 'text-orange-600' },
  furniture: { label: 'Arredi', icon: Sofa, color: 'text-purple-600' },
  subcontract: { label: 'Subappalto', icon: Building2, color: 'text-emerald-600' }
};

// Formattazione valuta
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const n0 = Number(value);
  const n = isNaN(n0) ? value : (Math.round(n0 * 100) === 0 ? 0 : n0);
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(n);
};

const EconomicImportModal = ({ 
  isOpen, 
  onClose, 
  projectId,
  onImportComplete
}) => {
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  
  // Selezioni
  const [selectedSources, setSelectedSources] = useState([]);
  
  // Carica fonti disponibili
  const fetchSources = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/economic-import-sources`);
      setSources(response.data);
      setSelectedSources([]);
    } catch (error) {
      console.error('Error fetching import sources:', error);
      toast.error('Errore nel caricamento delle fonti importabili');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchSources();
    }
  }, [isOpen, projectId, fetchSources]);

  // Toggle selezione fonte
  const toggleSource = (sourceType, sourceId) => {
    const key = `${sourceType}:${sourceId}`;
    setSelectedSources(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Seleziona tutti disponibili
  const selectAllAvailable = () => {
    if (!sources) return;
    
    const available = [];
    
    if (sources.contract?.import_status === 'available') {
      available.push(`contract:${sources.contract.source_id}`);
    }
    
    sources.extras?.forEach(e => {
      if (e.import_status === 'available') {
        available.push(`extra:${e.source_id}`);
      }
    });
    
    sources.furniture?.forEach(f => {
      if (f.import_status === 'available') {
        available.push(`furniture:${f.source_id}`);
      }
    });

    sources.subcontracts?.forEach(s => {
      if (s.import_status === 'available') {
        available.push(`subcontract:${s.source_id}`);
      }
    });
    
    setSelectedSources(available);
  };

  // Deseleziona tutti
  const deselectAll = () => {
    setSelectedSources([]);
  };

  // Esegui import
  const handleImport = async () => {
    if (selectedSources.length === 0) {
      toast.warning('Seleziona almeno una fonte da importare');
      return;
    }
    
    try {
      setImporting(true);
      
      const sourcesToImport = selectedSources.map(key => {
        const [type, id] = key.split(':');
        return { source_type: type, source_id: id };
      });
      
      const response = await api.post(`/projects/${projectId}/economic-import`, {
        sources: sourcesToImport
      });
      
      const result = response.data;
      
      if (result.imported_count > 0) {
        toast.success(`Importate ${result.imported_count} righe nel Quadro Economico`);
      }
      
      if (result.skipped_count > 0) {
        toast.info(`${result.skipped_count} fonti già importate (skip)`);
      }
      
      if (result.errors?.length > 0 && result.imported_count === 0) {
        toast.error('Errori durante l\'import: ' + result.errors.join(', '));
      }
      
      // Ricarica fonti per aggiornare stati
      await fetchSources();
      
      // Callback per aggiornare tabella principale
      if (onImportComplete) {
        onImportComplete(result);
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImporting(false);
    }
  };

  // Render singola fonte
  const renderSourceItem = (source, type) => {
    const TypeIcon = SOURCE_TYPE_LABELS[type]?.icon || FileText;
    const statusInfo = IMPORT_STATUS_LABELS[source.import_status] || IMPORT_STATUS_LABELS.not_importable;
    const StatusIcon = statusInfo.icon;
    const isAvailable = source.import_status === 'available';
    const isSelected = selectedSources.includes(`${type}:${source.source_id}`);
    
    return (
      <div 
        key={`${type}-${source.source_id}`}
        className={`flex items-start gap-3 p-3 border rounded-lg ${
          isAvailable ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-60'
        } ${isSelected ? 'border-[#FF7819] bg-orange-50' : ''}`}
        onClick={() => isAvailable && toggleSource(type, source.source_id)}
      >
        {/* Checkbox */}
        <Checkbox 
          className="mt-0.5 flex-shrink-0"
          checked={isSelected}
          disabled={!isAvailable}
          onCheckedChange={() => isAvailable && toggleSource(type, source.source_id)}
        />
        
        {/* Icona tipo */}
        <TypeIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${SOURCE_TYPE_LABELS[type]?.color || 'text-gray-500'}`} />
        
        {/* Info (contenuto flessibile) */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm break-words">{source.description}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
            <span className="break-all">Rif: {source.reference_number}</span>
            <span className="font-medium whitespace-nowrap">{formatCurrency(source.amount)}</span>
            <span className="text-gray-400 whitespace-nowrap">({source.amount_type === 'iva_esclusa' ? 'IVA esclusa' : 'IVA inclusa'})</span>
            {source.status_label && (
              <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 whitespace-nowrap">
                {source.status_label}
              </Badge>
            )}
          </div>
        </div>

        {/* Stato (colonna destra, mai troncato) */}
        <Badge className={`text-xs flex-shrink-0 whitespace-nowrap self-start ${statusInfo.color}`}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusInfo.label}
        </Badge>
      </div>
    );
  };

  // Contatori disponibili
  const getAvailableCount = () => {
    if (!sources) return 0;
    let count = 0;
    if (sources.contract?.import_status === 'available') count++;
    count += sources.extras?.filter(e => e.import_status === 'available').length || 0;
    count += sources.furniture?.filter(f => f.import_status === 'available').length || 0;
    count += sources.subcontracts?.filter(s => s.import_status === 'available').length || 0;
    return count;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#FF7819]" />
            Importa dati ufficiali
          </DialogTitle>
          <DialogDescription>
            Importa righe economiche da contratto, extra approvati e arredi confermati/ordinati.
            Le righe importate saranno modificabili nel Quadro Economico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Caricamento fonti...</span>
            </div>
          ) : (
            <>
              {/* Azioni globali */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-600">
                  {getAvailableCount()} fonti disponibili per l'import
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllAvailable}
                    disabled={getAvailableCount() === 0}
                  >
                    Seleziona tutti
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={deselectAll}
                    disabled={selectedSources.length === 0}
                  >
                    Deseleziona
                  </Button>
                </div>
              </div>

              {/* Sezione Contratto */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Contratto Lavori
                </h4>
                {sources?.contract ? (
                  renderSourceItem(sources.contract, 'contract')
                ) : (
                  <p className="text-sm text-gray-400 italic pl-6">Nessun contratto collegato</p>
                )}
              </div>

              {/* Sezione Extra */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Package className="w-4 h-4 text-orange-600" />
                  Extra Approvati
                  {sources?.extras?.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {sources.extras.length}
                    </Badge>
                  )}
                </h4>
                {sources?.extras?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {sources.extras.map(extra => renderSourceItem(extra, 'extra'))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic pl-6">Nessun extra approvato</p>
                )}
              </div>

              {/* Sezione Arredi */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Sofa className="w-4 h-4 text-purple-600" />
                  Arredi Confermati/Ordinati
                  {sources?.furniture?.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {sources.furniture.length}
                    </Badge>
                  )}
                </h4>
                {sources?.furniture?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {sources.furniture.map(fq => renderSourceItem(fq, 'furniture'))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic pl-6">Nessun arredo confermato/ordinato</p>
                )}
              </div>

              {/* Sezione Contratti di Subappalto */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                  Contratti di Subappalto
                  {sources?.subcontracts?.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {sources.subcontracts.length}
                    </Badge>
                  )}
                </h4>
                {sources?.subcontracts?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {sources.subcontracts.map(sc => renderSourceItem(sc, 'subcontract'))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic pl-6">Nessun contratto di subappalto ufficiale</p>
                )}
              </div>

              {/* Info box */}
              <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 p-3 rounded">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-700">Note sull'importazione:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Gli importi sono IVA esclusa (imponibile) dove disponibile</li>
                    <li>Le righe importate avranno costo stimato e consuntivo a 0 (da compilare)</li>
                    <li>Le righe restano completamente modificabili dopo l'import</li>
                    <li>L'import non modifica SAL, pagamenti cliente o total_amount progetto</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 px-6 py-4 bg-background border-t border-[#ECECEC] flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={importing}
            data-testid="import-cancel-btn"
          >
            Annulla
          </Button>
          <Button 
            onClick={handleImport}
            disabled={importing || selectedSources.length === 0}
            className="bg-[#FF7819] hover:bg-[#e56a10]"
            data-testid="import-confirm-btn"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importazione...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Importa {selectedSources.length > 0 ? `(${selectedSources.length})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EconomicImportModal;
